/**
 * Dashboards, as metadata.
 *
 * A dashboard is a list of widgets; each widget names a computation the server
 * knows how to run. Like reports, the numbers are code and the layout is data —
 * and like reports, nothing loads until the filters are applied (docs/03).
 */
export type WidgetKind = 'kpi' | 'split' | 'rank' | 'series' | 'gauge' | 'donut' | 'ratio' | 'health' | 'timeline'

export interface WidgetDef {
  key: string
  kind: WidgetKind
  title: string
  subtitle?: string
  /** Half width by default; a series usually wants the full row. */
  span?: 1 | 2
  /** How the value is written. */
  format?: 'money' | 'qty' | 'count' | 'pct'
  /**
   * How a series or split is drawn (docs/video-notes — every chart type the
   * reference uses): grouped columns, stacked columns, an area, lines, or a
   * split as a ring.
   */
  chart?: 'bar' | 'stacked' | 'area' | 'line' | 'donut'
  /**
   * The headline totals over a series act as series pickers: `Total order`
   * shows every series stacked, `Purchase order` shows only that one.
   */
  selectable?: boolean
  /**
   * A snapshot as of today — the reference's `- Current` cards. The period
   * filter does not apply to it; what is open is open whenever it was ordered.
   */
  current?: boolean
  /**
   * Which report filter a row of this widget sets when it is opened
   * (docs/03 — `Show details`).
   *
   * A bar naming a project opens that project's financials; one naming a
   * supplier opens what has been bought from them. Without it a dashboard is a
   * picture rather than a way in.
   */
  drillTo?: 'projectId' | 'supplierId' | 'jobTypeId'
  /** The report a row opens, where it is not the usual one for `drillTo`. */
  drillReport?: string
}

export interface DashboardFilterDef {
  key: string
  label: string
  /**
   * `companies` is the group view: a multi-select of the companies this person
   * belongs to, the current one ticked to begin with (docs/03 — `Company*`).
   */
  control: 'dateRange' | 'lookup' | 'checkbox' | 'companies'
  required?: boolean
  lookup?: { entity: string }
  /** Loads as soon as it changes, without an Apply — the project picker, a tick box. */
  autoApply?: boolean
  /**
   * The window a date range opens on. The reference's aggregate dashboards
   * open on the rolling year ending today, so a trend shows twelve months
   * whatever the date; `fy` is the financial year so far.
   */
  defaultPeriod?: 'fy' | 'last12Months'
}

export interface DashboardDef {
  key: string
  route: string
  label: string
  blurb: string
  filters: readonly DashboardFilterDef[]
  widgets: readonly WidgetDef[]
}

export const defineDashboard = <const T extends DashboardDef>(def: T): T => def

const PERIOD = { key: 'period', label: 'Period', control: 'dateRange', required: true, defaultPeriod: 'last12Months' } as const
const COMPANIES = { key: 'companyIds', label: 'Company', control: 'companies', required: true } as const

/*
  The order is the reference's (docs/video-notes/07): the two "- Current"
  snapshots, the two top-ten lists under them, billing against cost, the
  job-type and cost-type splits, then the two project trends.
*/
export const projectOverview = defineDashboard({
  key: 'project_overview', route: 'projects_dashboard',
  label: 'Project Overview dashboard',
  blurb: 'Provides an overview of all projects, including details on the open, billed, and unbilled values, incurred costs, and the total count of projects.',
  filters: [
    PERIOD,
    COMPANIES,
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
  ],
  widgets: [
    { key: 'open_order_value', kind: 'split', title: 'Open order value - Current', subtitle: 'Job type - split up', format: 'money', current: true, drillTo: 'jobTypeId' },
    { key: 'unbilled_value', kind: 'split', title: 'Unbilled value - Current', subtitle: 'Job type - split up', format: 'money', current: true, drillTo: 'jobTypeId' },
    { key: 'top_projects_open', kind: 'rank', title: 'Top 10 projects - By open order value', format: 'money', current: true, drillTo: 'projectId' },
    { key: 'top_projects_unbilled', kind: 'rank', title: 'Top 10 projects - By unbilled value', format: 'money', current: true, drillTo: 'projectId' },
    { key: 'billed_vs_cost', kind: 'series', title: 'Project billing vs cost', subtitle: 'Month on month trend on total cost spent vs project billed value', span: 2, format: 'money', chart: 'area' },
    { key: 'billed_by_job_type', kind: 'split', title: 'Billed value - By job type', subtitle: 'Job type - split up', format: 'money', drillTo: 'jobTypeId' },
    { key: 'cost_by_job_type', kind: 'split', title: 'Cost incurred - By job type', subtitle: 'Job type - split up', format: 'money', drillTo: 'jobTypeId' },
    { key: 'cost_by_type', kind: 'split', title: 'Cost incurred - By cost type', subtitle: 'Cost type - split up', format: 'money' },
    { key: 'project_counts', kind: 'series', title: 'Projects count - Trend', subtitle: 'Month on month trend of number of projects', span: 2, format: 'count', chart: 'bar' },
    // What has been won month by month, which is the question a management
    // meeting opens with and a single count of live projects cannot answer.
    { key: 'new_project_value', kind: 'series', title: 'New projects value', subtitle: 'Month on month trend of new projects value', span: 2, format: 'money' },
    // What the Financials report's Overview showed, moved here as the
    // reference says it will be (RPT-040): the last 30 days on the 30 before.
    { key: 'new_projects_30d', kind: 'kpi', title: 'New projects', subtitle: 'Last 30 days', format: 'count' },
    { key: 'new_project_value_30d', kind: 'kpi', title: 'Project value won', subtitle: 'Last 30 days', format: 'money' },
    { key: 'invoiced_value_30d', kind: 'kpi', title: 'Invoiced value', subtitle: 'Last 30 days', format: 'money' },
  ],
} as const)

export const projectsHealth = defineDashboard({
  key: 'projects_health', route: 'project_health_dashboard',
  label: 'Projects Health dashboard',
  blurb: 'Stagewise lists all ongoing projects along with their health status and relevant details.',
  filters: [
    { key: 'separateSubProjects', label: 'Show sub-projects separately', control: 'checkbox', autoApply: true },
  ],
  widgets: [
    { key: 'projects_health_list', kind: 'health', title: 'Ongoing projects', span: 2, format: 'money', drillTo: 'projectId' },
    // The calendar half of health. A job can be making money and still be
    // three months late, and only one of those shows up in a margin.
    { key: 'schedule_slippage', kind: 'rank', title: 'Projects by days late', format: 'count' },
    { key: 'overdue_tasks', kind: 'kpi', title: 'Work past its date', format: 'count' },
  ],
} as const)

export const projectPerformance = defineDashboard({
  key: 'project_performance', route: 'project_performance_dashboard',
  label: 'Project Performance dashboard',
  blurb: 'Provides a complete overview of project performance, including progress, cost, and revenue metrics.',
  filters: [
    { key: 'projectId', label: 'Project', control: 'lookup', required: true, lookup: { entity: 'project' }, autoApply: true },
    { key: 'excludeSubProjects', label: "Don't include sub-projects", control: 'checkbox', autoApply: true },
  ],
  widgets: [
    /*
      The reference's grid (docs/03 §3): the timeline and the progress ring
      share the first row, then contract value beside billing, cost beside
      profit, the S-curve across, and task status beside the cost summary.
      `Planned Start`, `Planned Finish`, `Forecast Finish`, `Delay (Days)`
      and the elapsed / remaining / additional legend under them — a project
      dashboard that says nothing about dates answers half the question.
    */
    { key: 'project_timeline', kind: 'timeline', title: 'Project timeline', format: 'count' },
    { key: 'progress_summary', kind: 'donut', title: 'Progress summary', format: 'pct' },
    { key: 'contract_value', kind: 'kpi', title: 'Contract value', format: 'money' },
    { key: 'billing_performance', kind: 'ratio', title: 'Billing performance', format: 'money' },
    { key: 'cost_performance', kind: 'ratio', title: 'Cost performance', format: 'money' },
    { key: 'profit_overview', kind: 'ratio', title: 'Profit overview', format: 'pct' },
    { key: 'progress_scurve', kind: 'series', title: 'Progress S-curve', subtitle: 'Planned against actual, cumulative', span: 2, format: 'pct', chart: 'line' },
    { key: 'task_status', kind: 'split', title: 'Task status', format: 'count', chart: 'donut' },
    { key: 'cost_summary', kind: 'kpi', title: 'Cost summary', format: 'money' },
    { key: 'cost_by_type', kind: 'split', title: 'Cost incurred - By cost type', format: 'money' },
    { key: 'schedule_performance', kind: 'gauge', title: 'Schedule performance', format: 'count' },
  ],
} as const)

export const purchaseOverview = defineDashboard({
  key: 'purchase_overview', route: 'purchase_dashboard',
  label: 'Purchase Overview dashboard',
  blurb: 'Overview about the purchases and subcontracts of the projects.',
  filters: [
    PERIOD,
    COMPANIES,
    { key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } },
  ],
  widgets: [
    { key: 'open_orders_value', kind: 'split', title: 'Open orders value', subtitle: 'Still to be received or billed', format: 'money', current: true },
    { key: 'open_orders_count', kind: 'split', title: 'Open orders count', subtitle: 'Orders with a balance open', format: 'count', current: true },
    { key: 'purchase_by_month', kind: 'series', title: 'Purchase value', subtitle: 'Month on month trend of new purchases - Value', span: 2, format: 'money', chart: 'stacked', selectable: true },
    { key: 'invoices_received_by_month', kind: 'series', title: 'Purchase invoice received', subtitle: 'Month on month trend of purchase invoices received', span: 2, format: 'money', chart: 'stacked', selectable: true },
    { key: 'order_count_by_month', kind: 'series', title: 'Order count', subtitle: 'Month on month trend of new purchase and subcontract orders', span: 2, format: 'count', chart: 'stacked', selectable: true },
    /*
      Savings is a signed number and is often negative — the demo tenant's own
      total is minus twenty-nine thousand. A widget that could only go up would
      be the wrong shape for the thing being measured.
    */
    { key: 'savings_by_month', kind: 'series', title: 'Purchase savings', subtitle: 'Month on month trend on purchase savings', format: 'money' },
    { key: 'top_buyers', kind: 'rank', title: 'Top Purchase executives - Purchase savings', format: 'money' },
    { key: 'top_suppliers', kind: 'rank', title: 'Top 10 Suppliers - value', format: 'money', drillTo: 'supplierId' },
    { key: 'top_subcontractors', kind: 'rank', title: 'Top 10 Subcontractors - By value', format: 'money', drillTo: 'supplierId' },
    { key: 'top_categories', kind: 'series', title: 'Top 5 material categories - By value', subtitle: 'Month on month trend of purchase of materials', span: 2, format: 'money', chart: 'line' },
  ],
} as const)

/**
 * docs/24 — the supply chain on one page: what to order now, what is late
 * to arrive, what is on the shelf and where, who is supplying well, which
 * stores are due a count, and where material is being wasted.
 *
 * Every figure is read off the report of the same name, and opens it.
 */
export const supplyChainOverview = defineDashboard({
  key: 'supply_chain', route: 'supply_chain_dashboard',
  label: 'Supply chain',
  blurb: 'What to order now, what is late, live stock by store and class, supplier scores, audits due and wastage.',
  filters: [
    { key: 'period', label: 'Orders placed between', control: 'dateRange' },
    { key: 'projectId', label: 'Project', control: 'lookup', lookup: { entity: 'project' } },
  ],
  widgets: [
    { key: 'sc_buy_now', kind: 'kpi', title: 'Material requirement plan', subtitle: 'Lines by when to order', span: 2, format: 'count' },
    { key: 'sc_order_by_class', kind: 'split', title: 'To order this week', subtitle: 'Late or due, by ABC class', format: 'count' },
    { key: 'sc_orders_late', kind: 'kpi', title: 'Late deliveries', subtitle: 'Orders past their promised date', format: 'count' },
    { key: 'sc_stock_by_store', kind: 'split', title: 'Live stock by store', subtitle: 'At what it cost', format: 'money' },
    { key: 'sc_stock_by_class', kind: 'split', title: 'Live stock by ABC class', subtitle: 'Where the money on the shelf is', format: 'money' },
    { key: 'sc_late_suppliers', kind: 'rank', title: 'Suppliers with late orders', subtitle: 'Orders past their date', format: 'count' },
    { key: 'sc_vendor_scores', kind: 'rank', title: 'Supplier scores', subtitle: 'Measured, out of 100', format: 'count', drillTo: 'supplierId', drillReport: 'vendor_scorecard' },
    { key: 'sc_audits_due', kind: 'kpi', title: 'Stock audits', subtitle: 'Counts due by the audit cycle', format: 'count' },
    { key: 'sc_wastage', kind: 'rank', title: 'Wastage by material', subtitle: 'Share of what was issued', format: 'pct' },
  ],
} as const)

export const DASHBOARD_DEFS = [
  projectOverview, projectsHealth, projectPerformance, purchaseOverview, supplyChainOverview,
] as const satisfies readonly DashboardDef[]

const byKey = new Map<string, DashboardDef>(DASHBOARD_DEFS.map((d) => [d.key, d]))
const byRoute = new Map<string, DashboardDef>(DASHBOARD_DEFS.map((d) => [d.route, d]))
export const getDashboardDef = (key: string): DashboardDef | undefined => byKey.get(key)
export const getDashboardDefByRoute = (route: string): DashboardDef | undefined => byRoute.get(route)
